import { DateTime } from "luxon";
import type React from "react";
import { getTimeColumnWidth } from "@/domains/appointment/appointments/config/calendar-config";
import { type TimeSlotConfig } from "@/domains/appointment/appointments/utils/calendar-utils";
import { TimeColumn } from "./TimeSlotGrid";

export interface CalendarGridProps {
  timeConfig: TimeSlotConfig;
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  timeColumnWidth?: string;
  minHeight?: string;
  enableScroll?: boolean;
  scrollBehavior?: "auto" | "smooth";
}

/**
 * Base calendar grid layout with time column
 */
// Create stable default values outside component
const DEFAULT_TIME_COLUMN_WIDTH = getTimeColumnWidth();

export function CalendarGrid({
  timeConfig,
  children,
  timeColumnWidth = DEFAULT_TIME_COLUMN_WIDTH,
  minHeight = "auto",
}: CalendarGridProps) {
  return (
    <div className={"border border-t-0 border-border rounded-b-lg"}>
      <div className="grid grid-cols-[auto_1fr]" style={minHeight !== "auto" ? { minHeight } : {}}>
        {/* Time Column */}
        <TimeColumn
          config={timeConfig}
          className={`${timeColumnWidth} flex-shrink-0`}
          mobileBreakpoint={timeColumnWidth.includes("sm:")}
        />

        {/* Content Area */}
        {children}
      </div>
    </div>
  );
}

/**
 * Multi-column calendar grid for week/3-day views
 */
export interface MultiColumnCalendarGridProps {
  timeConfig: TimeSlotConfig;
  numberOfColumns: number;
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  timeColumnWidth?: string;
  columnWidth?: string;
  minHeight?: string;
  enableHorizontalScroll?: boolean;
  enableVerticalScroll?: boolean;
  responsiveColumns?: boolean; // New prop for responsive column behavior
}

export function MultiColumnCalendarGrid({
  timeConfig,
  children,
  className = "",
  timeColumnWidth = DEFAULT_TIME_COLUMN_WIDTH,
  minHeight = "auto",
  enableHorizontalScroll = true,
  responsiveColumns = false,
}: MultiColumnCalendarGridProps) {
  return (
    <div
      className={`border border-t-0 border-border rounded-b-lg ${responsiveColumns && enableHorizontalScroll ? "overflow-x-auto sm:overflow-x-visible" : ""}`}
      style={{
        WebkitOverflowScrolling: enableHorizontalScroll ? "touch" : "auto",
      }}
      id="calendar-grid-container"
    >
      <div
        className={`flex ${enableHorizontalScroll && !responsiveColumns ? "min-w-max" : "w-full"} ${className}`}
        style={minHeight !== "auto" ? { minHeight } : {}}
      >
        {/* Time Column */}
        <TimeColumn
          config={timeConfig}
          className={`${timeColumnWidth} flex-shrink-0`}
          mobileBreakpoint={timeColumnWidth.includes("sm:")}
        />

        {/* Content Columns */}
        <div
          className={`flex ${
            responsiveColumns ? "w-full" : enableHorizontalScroll ? "min-w-max" : "flex-1"
          }`}
          id="calendar-content-columns"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Month calendar grid (7x6 grid for weeks)
 */
export interface MonthCalendarGridProps {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  weekdayHeaders?: string[];
  showWeekdayHeaders?: boolean;
}

// Create stable default values outside component
const DEFAULT_WEEKDAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function MonthCalendarGrid({
  children,
  className = "",
  containerClassName = "",
  weekdayHeaders = DEFAULT_WEEKDAY_HEADERS,
  showWeekdayHeaders = true,
}: MonthCalendarGridProps) {
  return (
    <div
      className={`border border-t-0 border-border rounded-b-lg overflow-hidden ${containerClassName}`}
    >
      {/* Days of Week Header */}
      {showWeekdayHeaders && (
        <div className="grid grid-cols-7 bg-muted">
          {weekdayHeaders.map((day) => (
            <div
              key={day}
              className="p-3 text-center text-sm font-medium text-foreground border-r border-border last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>
      )}

      {/* Calendar Grid */}
      <div className={`grid grid-cols-7 ${className}`}>{children}</div>
    </div>
  );
}

/**
 * Month day cell component
 */
export interface MonthDayCellProps {
  date: DateTime;
  isCurrentMonth?: boolean;
  isToday?: boolean;
  children?: React.ReactNode;
  className?: string;
  minHeight?: string;
  onClick?: (date: DateTime) => void;
}

export function MonthDayCell({
  date,
  isCurrentMonth = true,
  isToday = false,
  children,
  className = "",
  minHeight = "auto",
  onClick,
}: MonthDayCellProps) {
  const handleClick = () => {
    onClick?.(date);
  };

  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      {...(onClick ? { type: "button" as const } : {})}
      className={`p-2 border-r border-b border-border last:border-r-0 ${
        !isCurrentMonth ? "bg-muted" : ""
      } ${onClick ? "cursor-pointer hover:bg-accent" : ""} ${className}`}
      style={minHeight !== "auto" ? { minHeight } : {}}
      onClick={handleClick}
    >
      {/* Day Number */}
      <div
        className={`text-sm mb-2 ${
          !isCurrentMonth
            ? "text-muted-foreground"
            : isToday
              ? "font-bold text-primary bg-primary/10 w-6 h-6 rounded-full flex items-center justify-center"
              : "text-foreground"
        }`}
      >
        {date.day}
      </div>

      {/* Day Content */}
      {children}
    </Tag>
  );
}

/**
 * Simple appointment dot for month view
 */
export interface AppointmentDotProps {
  status: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

export function AppointmentDot({ status, className = "", onClick }: AppointmentDotProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
      case "accepted":
        return "bg-green-500";
      case "pending":
      case "waiting":
        return "bg-yellow-500";
      case "cancelled":
      case "declined":
        return "bg-red-500";
      case "completed":
        return "bg-blue-500";
      case "unassigned":
        return "bg-indigo-500";
      default:
        return "bg-gray-500";
    }
  };

  if (onClick) {
    return (
      <button
        type="button"
        className={`w-2 h-2 rounded-full ${getStatusColor(status)} ${className} cursor-pointer`}
        onClick={onClick}
      />
    );
  }

  return <div className={`w-2 h-2 rounded-full ${getStatusColor(status)} ${className}`} />;
}
